import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";
import { EntryFiltersModel } from "./use-entries-data.ts";

interface Props {
    id?: string;
    filters?: EntryFiltersModel;
}

export default function useEntriesReceiptData({ id, filters }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const entryReceiptQuery = useQuery({
        queryKey: [QueryKeys.GetEntryReceipt, { id }],
        queryFn: async () => await apis.engine.entries.entriesReceiptDetail(id!),
        enabled: id != undefined,
    });

    const needingReceiptQuery = useQuery({
        queryKey: [QueryKeys.GetNeedingReceipt, { filters }],
        queryFn: () => apis.engine.entries.entriesNeedingReceiptList(filters),
        enabled: filters != undefined,
    });

    const deleteReceiptMutation = useMutation({
        mutationFn: (id: string) => apis.engine.entries.entriesReceiptDelete(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntriesSearchList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntryTotals] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntryReceipt, { id }] });

            toast.success("Receipt deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete receipt"),
    });

    const uploadReceiptMutation = useMutation({
        mutationFn: ({ id, file }: { id: string; file: File }) =>
            apis.engine.entries.entriesReceiptCreate(id, { file }),
        onSuccess: (_data, { id }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetNeedingReceipt] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntry, { id }] });

            toast.success("Receipt uploaded");
        },
        onError: (error: Error) => handleApiError(error, "Failed to upload receipt"),
    });

    return {
        entryReceipt: entryReceiptQuery,
        needingReceipt: needingReceiptQuery,

        deleteReceipt: deleteReceiptMutation,
        uploadReceipt: uploadReceiptMutation,
    };
}
